import { Button, Modal, Spin, Result } from "antd";
import { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import Wrapper from "../../components/wrapper";
import config from "config";
import { api } from "util/api";
import "./clover-public-onboard.scss";
import GoopterLogo from "images/icons/goopter-logo.svg";

function CloverPublicOnboard(): JSX.Element {
    const intl = useIntl();
    const location = useLocation();
    // add loading state if needed
    const [loading, setLoading] = useState<boolean>(true);
    const [showOnboardButton, setShowOnboardButton] = useState<boolean>(true);
    const [isOnboardSuccess, setIsOnboardSuccess] = useState<boolean>(false);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const clientId = searchParams.get("client_id");
        const merchantId = searchParams.get("merchant_id");
        const authorizationCode = searchParams.get("code");
        if (authorizationCode) {
            setLoading(true);
            const cloverPublicOnboard = async (data: Record<string, any>) => {
                const response = await api("CLOVER_PUBLIC_ONBOARD")({
                    method: "POST",
                    body: JSON.stringify({ data }),
                });

                if (response?.RC === 200) {
                    setShowOnboardButton(false);
                    setIsOnboardSuccess(true);
                    setLoading(false);

                    // Remove query parameters after processing
                    const url = new URL(window.location.href);
                    url.search = "";
                    window.history.replaceState({}, "", String(url));
                } else {
                    Modal.error({
                        title: intl.formatMessage({ id: "error" }),
                        content: intl.formatMessage({ id: "clover_public_onboard_connect_error" }),
                    });
                    setShowOnboardButton(true);
                    setLoading(false);
                }
            };

            cloverPublicOnboard({
                client_id: clientId,
                authorization_code: authorizationCode,
                merchant_id: merchantId,
            });
        } else {
            // Show the onboard button if no authorization code is present
            setShowOnboardButton(true);
            setLoading(false);
        }
    }, [location.search]);

    const OnboardHandler = async () => {
        const searchParams = new URLSearchParams(location.search);
        const clientId = searchParams.get("client_id");
        const isLive =
            clientId === config.CLOVER.CARDPRESENT_CLIENT_ID.LIVE ||
            clientId === config.CLOVER.ECOMMERCE_CLIENT_ID.LIVE;

        const CLOVER_BASE_URL = isLive ? config.CLOVER.API_BASE_URL.LIVE : config.CLOVER.API_BASE_URL.SANDBOX;
        // Redirect to /settings/paymentOptions?tab=clover since this is the configured redirect URL in Clover app settings
        // const RIDIRECT_URL = `http://localhost:3000/settings/paymentOptions?tab=clover&client_id=${clientId}&from_public_onboard=true`;
        const RIDIRECT_URL = `${config.ROOT_URL}settings/paymentOptions?tab=clover&client_id=${clientId}&from_public_onboard=true`;
        const url = `${CLOVER_BASE_URL}/oauth/v2/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(
            RIDIRECT_URL
        )}`;
        window.open(url, "_blank");
    };

    const renderOnboardButton = () => {
        return (
            <Spin spinning={loading} size="large" className="loading-spinner">
                <Result
                    icon={<img className="goopter-logo" src={GoopterLogo} alt="Goopter Logo" />}
                    title={intl.formatMessage({ id: "clover_public_onboard_welcome" })}
                    subTitle={intl.formatMessage({ id: "clover_public_onboard_welcome_sub" })}
                    extra={
                        <Button type="primary" size="large" onClick={() => OnboardHandler()}>
                            <FormattedMessage id="clover_public_onboard_button" />
                        </Button>
                    }
                />
            </Spin>
        );
    };

    const renderOnboardSuccess = () => {
        return (
            <Result
                status="success"
                title={intl.formatMessage({ id: "clover_public_onboard_success" })}
                subTitle={intl.formatMessage({ id: "clover_public_onboard_success_sub" })}
            />
        );
    };

    return (
        <Wrapper helmet={{ title: "clover_public_onboard" }} public={true}>
            <div className="clover-public-onboard">
                {showOnboardButton && renderOnboardButton()}
                {isOnboardSuccess && renderOnboardSuccess()}
            </div>
        </Wrapper>
    );
}

export default CloverPublicOnboard;
